import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          // Verify token by fetching current user
          const response = await api.get('/auth/me');
          if (response.data.success) {
            setUser(response.data.data.user);
            localStorage.setItem('user', JSON.stringify(response.data.data.user));
          } else {
            // Token invalid, clear storage
            logout();
          }
        } catch (error) {
          // Token invalid or expired
          logout();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);

      if (response.data.success) {
        const { user: userData, token } = response.data.data;

        // Store token and user in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));

        setUser(userData);
        return { success: true, user: userData };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      const message =
        error.apiHint ||
        error.response?.data?.message ||
        error.message ||
        'Login failed';
      throw new Error(message);
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/auth/signup', userData);

      if (response.data.success) {
        const { user: newUser, token } = response.data.data;

        // Store token and user in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(newUser));

        setUser(newUser);
        return { success: true, user: newUser };
      } else {
        throw new Error(response.data.message || 'Signup failed');
      }
    } catch (error) {
      const message = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        error.message || 'Signup failed';
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const response = await api.post('/auth/google', { idToken });

      if (response.data.success) {
        const { user: userData, token } = response.data.data;

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));

        setUser(userData);
        return { success: true, user: userData };
      }

      throw new Error(response.data.message || 'Google sign-in failed');
    } catch (error) {
      const message =
        error.apiHint ||
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        error.message ||
        'Google sign-in failed';
      throw new Error(message);
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      if (response.data.success) {
        return response.data.data?.message || response.data.message || 'Check your email for next steps.';
      }

      throw new Error(response.data.message || 'Request failed');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        error.message ||
        'Request failed';
      throw new Error(message);
    }
  };

  const resetPasswordWithToken = async (token, password) => {
    try {
      const response = await api.post('/auth/reset-password', { token, password });

      if (response.data.success) {
        return response.data.data?.message || response.data.message || 'Password updated.';
      }

      throw new Error(response.data.message || 'Reset failed');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        error.message ||
        'Reset failed';
      throw new Error(message);
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', { oldPassword, newPassword });
      if (response.data.success) {
        return response.data.message || 'Password updated successfully.';
      }
      throw new Error(response.data.message || 'Failed to change password');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to change password';
      throw new Error(message);
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    loginWithGoogle,
    requestPasswordReset,
    resetPasswordWithToken,
    changePassword,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
